<template>
  <div class="vc-support-ticket-form">
    <div v-if="!isContainerInited" class="d-flex align-items-center justify-content-center" style="min-height:200px">
      <div class="spinner-border mx-2" role="status">
        <span class="visually-hidden">{{ $t('messages.loading') }}</span>
      </div>
      {{ $t('messages.loading') }}
    </div>

    <div v-else-if="schemaError" class="alert alert-danger" role="alert">
      {{ schemaError }}
    </div>

    <!-- Success state: route the user forward (view/track the ticket), not back into another submission. -->
    <div v-else-if="submitResult" class="card border-0 shadow-sm">
      <!-- Real success: a positive ticket id came back. -->
      <div v-if="submitResult.ticketId > 0" class="card-body text-center py-5">
        <i class="bi bi-check-circle-fill text-success" style="font-size:2.5rem;"></i>
        <h4 class="mt-3">{{ $t('messages.successTitle') }}</h4>
        <p class="text-muted mb-4">{{ $t('messages.successBody', [submitResult.ticketId]) }}</p>
        <div v-if="hasSession" class="d-flex justify-content-center gap-2">
          <button type="button" class="btn btn-primary"
            @click="$emit('view-ticket', submitResult.ticketId)">
            {{ $t('messages.viewTicket') }}
          </button>
          <button type="button" class="btn btn-outline-secondary" @click="$emit('back-to-list')">
            {{ $t('messages.backToList') }}
          </button>
        </div>
        <button v-else type="button" class="btn btn-outline-primary" @click="resetForm">
          {{ $t('messages.submitAnother') }}
        </button>
      </div>
      <!-- No ticket id: the server accepted the request but created no ticket (honeypot/quality
           silent-drop, or an unreadable Zendesk id). Don't claim a ticket was created (that would
           show "#null" and a false confirmation to a real user), but don't accuse them of being a
           bot either - a false positive should read as a neutral "not confirmed, try again". -->
      <div v-else class="card-body text-center py-5">
        <i class="bi bi-exclamation-circle text-warning" style="font-size:2.5rem;"></i>
        <h4 class="mt-3">{{ $t('messages.notConfirmedTitle') }}</h4>
        <p class="text-muted mb-4">{{ $t('messages.notConfirmedBody') }}</p>
        <button type="button" class="btn btn-primary" @click="resetForm">
          {{ $t('messages.tryAgain') }}
        </button>
      </div>
    </div>

    <!-- AI pre-submit check: user got their answer and chose not to submit -->
    <div v-else-if="answeredByAi" class="card border-0 shadow-sm">
      <div class="card-body text-center py-5">
        <i class="bi bi-check-circle-fill text-success" style="font-size:2.5rem;"></i>
        <p class="text-muted mt-3 mb-4">{{ $t('messages.aiAnsweredThanks') }}</p>
        <button type="button" class="btn btn-outline-primary" @click="resetForm">{{ $t('messages.submitAnother') }}</button>
      </div>
    </div>

    <!-- Form -->
    <div v-else class="card border-0 shadow-sm">
      <div class="card-body p-4">
        <h4 class="mb-3">{{ $t('messages.title') }}</h4>

        <div v-if="hasSession" class="alert alert-light border d-flex align-items-center py-2 mb-3">
          <i class="bi bi-person-check me-2"></i>
          <span class="small">{{ $t('messages.signedInAs', [displayEmail]) }}</span>
        </div>
        <div v-else class="alert alert-light border d-flex align-items-center py-2 mb-3">
          <i class="bi bi-info-circle me-2"></i>
          <span class="small">
            {{ $t('messages.signInPrompt') }}
            <a href="#" @click.prevent="window.dialogManager?.showSignIn()">{{ $t('messages.signIn') }}</a>
            {{ $t('messages.signInBenefit') }}
          </span>
        </div>

        <div v-if="submitError" class="alert alert-danger py-2" role="alert">{{ submitError }}</div>

        <form @submit.prevent="submitTicket">
          <fieldset class="mb-4">
          <legend class="form-label fw-semibold fs-sm mb-2">{{ $t('messages.issueType') }}</legend>
          <div class="support-topic-grid">
            <button v-for="it in availableIssueTypes" :key="it.id" type="button" class="support-topic-option"
            :class="{ active: issueTypeId === it.id }" :disabled="submitting" @click="selectIssueType(it.id)">
            <span class="support-topic-title">{{ issueTypeLabel(it) }}</span>
            <span class="support-topic-description">{{ issueTypeDescription(it) }}</span>
            </button>
          </div>
          <div v-if="requiresSignIn" class="form-text text-warning">
            <i class="bi bi-exclamation-triangle"></i> {{ $t('messages.requiresSignIn') }}
          </div>
          </fieldset>

          <div v-if="urlIssueType && !issueTypeId && schema" class="alert alert-warning" role="alert">
            {{ $t('messages.requestedTopicUnavailable') }}
          </div>

          <div v-if="!issueTypeId" class="support-topic-empty">
          {{ $t('messages.chooseTopicPrompt') }}
          </div>

          <template v-else>

          <div class="mb-3">
            <label class="form-label fw-semibold fs-sm mb-1">{{ $t('messages.subject') }}</label>
            <input ref="subjectInput" type="text" class="form-control" v-model="subject" :placeholder="$t('messages.subjectPlaceholder')"
              maxlength="200" required :disabled="submitting" />
          </div>

          <div class="mb-3">
            <label class="form-label fw-semibold fs-sm mb-1">{{ $t('messages.priority') }}</label>
            <select class="form-select" v-model="priority" :disabled="submitting">
              <option value="low">{{ $t('messages.priorityLow') }}</option>
              <option value="normal">{{ $t('messages.priorityNormal') }}</option>
              <option value="high">{{ $t('messages.priorityHigh') }}</option>
              <option value="urgent">{{ $t('messages.priorityUrgent') }}</option>
            </select>
          </div>

          <div class="mb-3">
            <label class="form-label fw-semibold fs-sm mb-1">{{ $t('messages.description') }}</label>
            <textarea ref="descriptionInput" class="form-control" rows="5" v-model="description"
              :placeholder="$t('messages.descriptionPlaceholder')" required :disabled="submitting"></textarea>
            <div v-if="miscategorizationHint" class="form-text text-warning">
              <i class="bi bi-lightbulb"></i> {{ $t('messages.miscategorizationHint') }}
            </div>
          </div>

          <div v-for="field in currentFields" :key="field.name" class="mb-3">
            <label class="form-label fw-semibold fs-sm mb-1">
              {{ fieldLabel(field) }} <span v-if="field.required" class="text-danger">*</span>
            </label>
            <div v-if="field.name === 'query_url'" class="support-query-url-row">
              <input type="text" class="form-control" v-model="fieldValues[field.name]"
                :required="field.required" :disabled="submitting || testingQueryUrl" />
              <button type="button" class="btn btn-outline-primary" :disabled="submitting || testingQueryUrl || !fieldValues.query_url"
                @click="testQueryUrl">
                <span v-if="testingQueryUrl" class="spinner-border spinner-border-sm me-1" role="status"></span>
                {{ testingQueryUrl ? $t('messages.testingQueryUrl') : $t('messages.testQueryUrl') }}
              </button>
            </div>
            <textarea v-else-if="field.name === 'sample_data'" class="form-control" rows="6" v-model="fieldValues[field.name]"
              :required="field.required" :disabled="submitting"></textarea>
            <input v-else type="text" class="form-control" v-model="fieldValues[field.name]"
              :required="field.required" :disabled="submitting" />
            <div v-if="field.name === 'query_url' && queryTestResult" class="support-query-test-result mt-2"
              :class="queryTestResult.ok ? 'support-query-test-success' : 'support-query-test-error'">
              <strong>{{ queryTestResult.title }}</strong>
              <div>{{ queryTestResult.message }}</div>
            </div>
          </div>

          <div v-if="!hasSession" class="mb-3">
            <label class="form-label fw-semibold fs-sm mb-1">{{ $t('messages.email') }}</label>
            <!-- Static placeholder, not translated: an email address contains '@', which the
                 vue-i18n message compiler parses as linked-message syntax and throws on. -->
            <input type="email" class="form-control" v-model="email" placeholder="you@example.com"
              required :disabled="submitting" />
            <div class="form-text">{{ $t('messages.emailHint') }}</div>
          </div>

          <div class="mb-3">
            <div class="support-attachment-heading">
              <label class="form-label fw-semibold fs-sm mb-0">{{ $t('messages.attachments') }}</label>
              <button type="button" class="btn btn-outline-primary btn-sm" :disabled="submitting || attachments.length >= maxAttachments"
                @click="$refs.attachmentInput.click()">
                <i class="bi bi-paperclip me-1"></i>{{ $t('messages.addAttachment') }}
              </button>
            </div>
            <div v-if="attachments.length" class="list-group mb-2">
              <div v-for="a in attachments" :key="a.name + a.status" class="list-group-item d-flex align-items-center justify-content-between py-2">
                <span class="small text-truncate me-2">
                  <span v-if="a.status === 'uploading'" class="spinner-border spinner-border-sm me-1"></span>
                  <i v-else-if="a.status === 'error'" class="bi bi-exclamation-triangle text-danger me-1"></i>
                  <i v-else class="bi bi-paperclip me-1"></i>
                  {{ a.name }}
                  <span v-if="a.status === 'error'" class="text-danger"> - {{ a.error }}</span>
                </span>
                <button type="button" class="btn btn-sm btn-outline-secondary" :disabled="a.status === 'uploading'"
                  @click="removeAttachment(a)">{{ $t('messages.removeAttachment') }}</button>
              </div>
            </div>
            <input type="file" ref="attachmentInput" class="d-none" multiple @change="onAttachmentFilesSelected" />
            <div class="form-text">{{ $t('messages.attachmentLimitHint', [maxAttachments]) }}</div>
          </div>

          <!-- Honeypot: hidden from real users, bots that fill it get a fake success server-side.
               No name attribute + autocomplete="new-password" + data-lpignore/data-1p-ignore make it
               invisible to browser autofill and password-manager form fillers, which were populating
               it and causing legitimate submissions to be silently dropped server-side. Read from the
               DOM at submit time (not v-model) so only a real fill is sent. -->
          <input type="text" ref="honeypotInput" class="vc-support-ticket-honeypot" tabindex="-1"
                 autocomplete="new-password" data-lpignore="true" data-1p-ignore="true"
                 data-bwignore="true" data-form-type="other" aria-hidden="true" />

          <div class="support-submit-panel">
          <div v-if="triageResult" ref="aiReviewResult" class="support-ai-review-result" tabindex="-1">
          <div class="support-ai-review-kicker"><i class="bi bi-stars me-1"></i>{{ $t('messages.aiReviewResultTitle') }}</div>
            <div v-if="triageResult.canAnswerImmediately" class="mb-3">
            <h5 class="mb-2"><i class="bi bi-lightbulb me-1 text-warning"></i>{{ $t('messages.aiPossibleAnswerTitle') }}</h5>
            <div v-html="triageResult.suggestedAnswerHtml"></div>
            </div>
            <div v-if="suggestedIssueType" class="mb-3">
              <h6 class="mb-2">{{ $t('messages.aiSuggestedTemplateTitle') }}</h6>
              <div class="d-flex flex-wrap align-items-center gap-2">
              <span>{{ $t('messages.aiSuggestedTemplateBody', [issueTypeLabel(suggestedIssueType)]) }}</span>
              <button type="button" class="btn btn-sm btn-outline-primary" @click="applySuggestedIssueType">
                {{ $t('messages.aiUseSuggestedTemplate') }}
              </button>
              </div>
            </div>
            <div v-if="!triageResult.isClear" class="mb-3">
            <h6 class="mb-2">{{ $t('messages.aiMissingInfoTitle') }}</h6>
            <ul v-if="triageResult.missingInfo && triageResult.missingInfo.length" class="mb-0">
              <li v-for="(item, idx) in triageResult.missingInfo" :key="idx">{{ item }}</li>
            </ul>
            <p v-else class="mb-0 text-muted small">{{ $t('messages.aiMissingInfoFallback') }}</p>
            </div>
            <div v-if="triageResult.isClear && !triageResult.canAnswerImmediately" class="mb-3 text-muted">
            <i class="bi bi-check-circle text-success me-1"></i>{{ $t('messages.aiLooksGood') }}
            </div>
          </div>
            <div class="support-submit-row">
            <button v-if="!triageResult" type="submit" class="btn btn-primary" :disabled="submitting || !canSubmit || hasUploadingAttachments || checkingTicket">
                <span v-if="submitting || checkingTicket" class="spinner-border spinner-border-sm me-1" role="status"></span>
                {{ actionButtonLabel }}
              </button>
            <button v-if="triageResult && triageResult.canAnswerImmediately" type="button" class="btn btn-primary" @click="answeredByAi = true">
            {{ $t('messages.aiAnswerHelped') }}
            </button>
            <button v-if="triageResult" type="submit" class="btn btn-primary" :disabled="submitting || !canSubmit || hasUploadingAttachments || checkingTicket">
            <span v-if="checkingTicket" class="spinner-border spinner-border-sm me-1" role="status"></span>
            {{ checkingTicket ? $t('messages.checkingTicket') : $t('messages.reviewAgain') }}
            </button>
            <button v-if="triageResult" type="button" class="btn btn-outline-primary" :disabled="submitting" @click="doSubmit(true)">
            <span v-if="submitting" class="spinner-border spinner-border-sm me-1"></span>
            {{ submitting ? $t('messages.submitting') : (triageResult.isClear ? $t('messages.submit') : $t('messages.aiSubmitAnyway')) }}
            </button>
              <div class="form-check support-ai-check">
                <input id="support-ai-review" class="form-check-input" type="checkbox" v-model="useAiReview" :disabled="submitting || checkingTicket" />
                <label class="form-check-label" for="support-ai-review">{{ $t('messages.useAiReview') }}</label>
              </div>
            </div>
            <div class="support-ai-note">{{ $t('messages.aiPrivacyNotice') }}</div>
          </div>
            </template>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import "/src/visualcrossing-core/vccommunicator_support";
import translations from '@/translations/supportticket.js';
import attachmentsMixin from '@/mixins/supportTicketAttachmentsMixin.js';

/**
 * Support ticket create form (design doc §12.2/§12.3: Mode A + Mode B in one component).
 * Renders entirely from the server-provided schema (GET services/supportticket/schema) so new
 * issue types/fields never require a change here - see docs/zendesk-support-portal-design.md §9.1.
 * List/thread/reply (the "My Tickets" portal, §8) are a separate follow-up component.
 */
export default {
  name: 'SupportTicketForm',
  mixins: [attachmentsMixin],
  i18n: {
    messages: {
      en: translations.en,
      de: translations.de,
    },
  },
  props: {
    // Optional: if the host page has already loaded the Google reCAPTCHA v3 script with a site
    // key, pass it here so anonymous submissions attach a token. Not required for Mode A, and
    // anonymous submissions without a token are correctly rejected server-side (design doc §7.3
    // /§6 - a missing token never bypasses verification, regardless of server config).
    recaptchaSiteKey: {
      type: String,
      default: null,
    },
    // Set by the AI assistant handoff only. When absent, ai_followup is hidden as a normal user choice.
    aiFollowupContext: {
    type: Object,
    default: null,
    },
  },
  data() {
    return {
      isContainerInited: false,
      sessionId: null,
      sessionState: null,
      schema: null,
      schemaError: null,
      issueTypeId: '',
      urlIssueType: '',
      urlFocusPending: false,
      fieldValues: {},
      subject: '',
      priority: 'normal',
      checkingTicket: false,
      triageResult: null,
      answeredByAi: false,
      useAiReview: true,
      testingQueryUrl: false,
      queryTestResult: null,
      description: '',
      email: '',
      submitting: false,
      submitError: null,
      submitResult: null,
      // Minimal, self-contained label maps for the current schema (design doc §9.1 leaves the
      // exact i18n mechanism for server-driven labels open); humanize() covers anything new
      // added server-side later without requiring a client change.
      issueTypeLabels: {
      business_enquiry: 'Sales Enquiry',
        account_issue: 'Account Issue',
        website_issue: 'Website Issue',
        weather_data_issue: 'Weather Data Issue',
        weather_api_issue: 'Weather API Issue',
        ai_followup: 'AI Assistant Follow-up',
      },
      issueTypeDescriptions: {
      business_enquiry: 'Sales, licensing, partnerships, or commercial questions.',
      account_issue: 'Login, billing, subscription, or account access help.',
      website_issue: 'A problem using the website or web tools.',
      weather_data_issue: 'Weather values, forecasts, history, locations, or dates.',
      weather_api_issue: 'API requests, query URLs, responses, errors, or integrations.',
      ai_followup: 'Continue from an AI assistant conversation.',
      },
      fieldLabels: {
        locations_dates: 'Location(s) and Date(s)',
        sample_data: 'Sample Data',
        query_url: 'Query URL',
      },
    };
  },
  computed: {
    urlFocusReady() {
      return this.urlFocusPending && this.isContainerInited && !!this.issueTypeId && !!this.schema && !this.schemaError;
    },
    hasSession() {
      return !!this.sessionId;
    },
    displayEmail() {
      return (this.sessionState && this.sessionState.subscriptionParams && this.sessionState.subscriptionParams.custEmail)
        || (this.sessionState && this.sessionState.userId)
        || '';
    },
    issueTypes() {
      return (this.schema && this.schema.issueTypes) || [];
    },
    availableIssueTypes() {
      var types = this.hasSession ? this.issueTypes : this.issueTypes.filter(it => it.anonymousAllowed);
      if (!this.hasAiFollowupContext) {
      types = types.filter(it => it.id !== 'ai_followup');
      }
      var order = ['business_enquiry', 'account_issue', 'weather_data_issue', 'weather_api_issue', 'ai_followup', 'website_issue'];
      return types.slice().sort(function (a, b) {
      var ai = order.indexOf(a.id);
      var bi = order.indexOf(b.id);
      return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
      });
    },
    hasAiFollowupContext() {
      var context = this.aiFollowupContext;
      if (!context) return false;
      return !!(context.userQuestion || context.botAnswer || context.threadId || context.assistantId);
    },
    currentIssueType() {
      return this.issueTypes.find(it => it.id === this.issueTypeId) || null;
    },
    suggestedIssueType() {
      if (!this.triageResult || !this.triageResult.suggestedIssueType || this.triageResult.suggestedIssueType === this.issueTypeId) return null;
      return this.availableIssueTypes.find(it => it.id === this.triageResult.suggestedIssueType) || null;
    },
    currentFields() {
      var fields = (this.currentIssueType && this.currentIssueType.fields) || [];
      return fields.filter(f => f.name !== 'resolutions');
    },
    requiresSignIn() {
      return !this.hasSession && this.currentIssueType && !this.currentIssueType.anonymousAllowed;
    },
    miscategorizationHint() {
      // Client-side mirror of the server's soft hint (design doc §9.2) so the user sees it before
      // submitting, not just after; the server re-checks and tags the ticket regardless.
      if (!this.currentIssueType || this.currentIssueType.fields.length > 0) return false;
      return /\b(api ?key|query url|forecast|historical data|error code|station id|timeline api)\b/i.test(this.description);
    },
    // Drives the submit button's disabled/greyed-out look - mirrors (but doesn't replace) the
    // server's own validation, so the button visibly reflects "not ready yet" rather than relying
    // solely on the browser's native required-field popup.
    canSubmit() {
      if (!this.issueTypeId || this.requiresSignIn) return false;
      if (!this.subject.trim() || !this.description.trim()) return false;
      if (!this.hasSession && !this.email.trim()) return false;
      return this.currentFields.every(f => !f.required || (this.fieldValues[f.name] || '').trim());
    },
    actionButtonLabel() {
      if (this.checkingTicket) return this.$t('messages.checkingTicket');
      if (this.submitting) return this.$t('messages.submitting');
      return this.useAiReview ? this.$t('messages.reviewTicket') : this.$t('messages.submit');
    },
  },
  watch: {
    urlFocusReady(ready) {
      if (!ready) return;
      this.$nextTick(() => {
        var input = this.subject.trim() ? this.$refs.descriptionInput : this.$refs.subjectInput;
        if (input) {
          input.focus();
          this.urlFocusPending = false;
        }
      });
    },
  },
  methods: {
    issueTypeLabel(issueType) {
      var issueTypeId = typeof issueType === 'string' ? issueType : issueType.id;
      var labelKey = typeof issueType === 'string' ? issueType : issueType.labelKey;
      return this.issueTypeLabels[issueTypeId] || this.humanize(labelKey);
    },
    fieldLabel(field) {
      return this.fieldLabels[field.name] || this.humanize(field.labelKey);
    },
    issueTypeDescription(issueType) {
      return this.issueTypeDescriptions[issueType.id] || '';
    },
    selectIssueType(issueTypeId) {
      this.issueTypeId = issueTypeId;
      this.triageResult = null;
    },
    applyUrlIssueType() {
      if (!this.urlIssueType || this.issueTypeId) return;
      var normalize = value => (value || '').trim().toLowerCase().replace(/[\s_-]+/g, '');
      var requested = normalize(this.urlIssueType);
      var match = this.availableIssueTypes.find(it =>
        [it.id, this.issueTypeLabel(it), this.humanize(it.labelKey)]
          .some(value => normalize(value) === requested));
      if (match) {
        this.selectIssueType(match.id);
        this.urlIssueType = '';
        this.urlFocusPending = true;
      }
    },
    humanize(labelKey) {
      var last = (labelKey || '').split('.').pop() || '';
      return last.split('_').map(function (w) { return w.charAt(0).toUpperCase() + w.slice(1); }).join(' ');
    },
    resetIssueTypeIfNeeded() {
      var available = this.availableIssueTypes;
      if (!available.length) return;
      if (this.hasAiFollowupContext && available.some(it => it.id === 'ai_followup')) {
        this.issueTypeId = 'ai_followup';
        return;
      }
      if (this.issueTypeId && !available.some(it => it.id === this.issueTypeId)) {
      this.issueTypeId = '';
      }
    },
    applySuggestedIssueType() {
      if (!this.suggestedIssueType) return;
      this.issueTypeId = this.suggestedIssueType.id;
      this.triageResult = null;
    },
    loadSchema() {
      var me = this;
      me.schemaError = null;
      window.vcCommunicator.getSupportTicketSchema()
        .then(function (result) {
          me.schema = result;
          me.resetIssueTypeIfNeeded();
          me.applyUrlIssueType();
        })
        .catch(function (error) {
          me.schemaError = 'Unable to load the support form right now. ' + error;
        });
    },
    // Reads the honeypot straight from the DOM at submit time. Not v-model bound, so autofill /
    // password-manager fills (which don't fire input events reliably) are still captured, while a
    // normal user's keystrokes can't reach the off-screen field. Returns '' when untouched.
    readHoneypot() {
      var el = this.$refs.honeypotInput;
      return el && el.value ? el.value : '';
    },
    resetForm() {
      this.submitResult = null;
      this.submitError = null;
      this.subject = '';
      this.priority = 'normal';
      this.useAiReview = true;
      this.testingQueryUrl = false;
      this.queryTestResult = null;
      this.description = '';
      this.fieldValues = {};
      if (this.$refs.honeypotInput) this.$refs.honeypotInput.value = '';
      this.triageResult = null;
      this.answeredByAi = false;
      this.resetAttachments();
      this.resetIssueTypeIfNeeded();
    },
    async getRecaptchaToken() {
      if (!window.grecaptcha || !window.grecaptcha.execute) {
        return null;
      }
      var siteKey = this.recaptchaSiteKey || this.detectRecaptchaSiteKey();
      if (!siteKey) {
        return null;
      }
      try {
        return await window.grecaptcha.execute(siteKey, { action: 'submit_ticket' });
      } catch (e) {
        console.error('reCAPTCHA execute failed:', e);
        return null;
      }
    },
    // The WordPress plugin (and the signup page) load reCAPTCHA v3 globally via
    // api.js?render=<siteKey>. When the host page doesn't pass the key as a prop, recover it from
    // the already-loaded script tag so anonymous submission still gets a token.
    detectRecaptchaSiteKey() {
      var scripts = document.querySelectorAll('script[src*="recaptcha/api.js"]');
      for (var i = 0; i < scripts.length; i++) {
        var match = /[?&]render=([^&]+)/.exec(scripts[i].src || '');
        if (match && match[1] && match[1] !== 'explicit') {
          return match[1];
        }
      }
      return null;
    },
    buildFieldsPayload() {
      var me = this;
      var fields = {};
      me.currentFields.forEach(function (f) { fields[f.name] = me.fieldValues[f.name] || ''; });
      return fields;
    },
    buildAiFollowupPayload() {
      if (this.issueTypeId !== 'ai_followup' || !this.hasAiFollowupContext) return null;
      return {
        userQuestion: this.aiFollowupContext.userQuestion || '',
        botAnswer: this.aiFollowupContext.botAnswer || '',
        threadId: this.aiFollowupContext.threadId || '',
        assistantId: this.aiFollowupContext.assistantId || '',
      };
    },
    async testQueryUrl() {
      var me = this;
      var url = (me.fieldValues.query_url || '').trim();
      if (!url) return;

      me.testingQueryUrl = true;
      me.queryTestResult = null;
      try {
        var controller = new AbortController();
        var timeout = window.setTimeout(function () { controller.abort(); }, 20000);
        var response = await fetch(url, {
          method: 'GET',
          cache: 'no-cache',
          signal: controller.signal,
        });
        window.clearTimeout(timeout);

        var bodyText = await response.text();
        var formattedBody = me.formatQueryResponseBody(bodyText);
        var sample = 'HTTP ' + response.status + ' ' + response.statusText + '\n\n' + formattedBody;
        me.fieldValues.sample_data = me.truncateSampleData(sample);
        me.queryTestResult = {
          ok: response.ok,
          title: response.ok ? me.$t('messages.queryTestSuccess') : me.$t('messages.queryTestHttpError'),
          message: 'HTTP ' + response.status + ' ' + response.statusText,
        };
      } catch (error) {
        var message = error && error.name === 'AbortError'
          ? me.$t('messages.queryTestTimeout')
          : (error && error.message ? error.message : '' + error);
        me.fieldValues.sample_data = me.truncateSampleData('Request failed\n\n' + message);
        me.queryTestResult = {
          ok: false,
          title: me.$t('messages.queryTestFailed'),
          message: message,
        };
      } finally {
        me.testingQueryUrl = false;
      }
    },
    formatQueryResponseBody(bodyText) {
      try {
        return JSON.stringify(JSON.parse(bodyText), null, 2);
      } catch (e) {
        return bodyText || '';
      }
    },
    truncateSampleData(sample) {
      var maxLength = 12000;
      if (!sample || sample.length <= maxLength) return sample;
      return sample.substring(0, maxLength) + '\n\n[Response truncated for ticket form]';
    },
    // Runs the AI pre-submit check (advisory, best-effort). Any failure or an all-clear result
    // proceeds straight to the real submission - only a flagged result pauses for user input.
    // Always stops at the triage panel for the user to press Submit explicitly - even a fully
    // clear/no-answer result - rather than auto-submitting, so the AI check's outcome is always
    // visible (and testable) instead of silently passing through most of the time.
    async submitTicket() {
      var me = this;
      if (!me.useAiReview) {
        return me.doSubmit(false);
      }
      me.checkingTicket = true;
      me.submitError = null;
      try {
        var payload = {
          issueType: me.issueTypeId,
          subject: me.subject,
          priority: me.priority,
          description: me.description,
          fields: me.buildFieldsPayload(),
          aiFollowup: me.buildAiFollowupPayload(),
        };
        var triage = await window.vcCommunicator.triageSupportTicket(payload);
        me.checkingTicket = false;
        if (!triage) {
          return me.doSubmit(false);
        }
        me.triageResult = triage;
        me.scrollReviewIntoView();
        return;
      } catch (error) {
        me.checkingTicket = false;
        // Advisory check itself failed (network/config issue, not a flagged ticket) - never let
        // that block a real ticket submission; the submit endpoint will still run code-only checks.
        return me.doSubmit(false);
      }
    },
      scrollReviewIntoView() {
        this.$nextTick(function () {
        var el = this.$refs.aiReviewResult;
        if (!el) return;
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.focus({ preventScroll: true });
        });
      },
    async doSubmit(aiReviewCompleted) {
      var me = this;
      me.submitError = null;
      me.submitting = true;
      try {
        var recaptchaToken = await me.getRecaptchaToken();
        var payload = {
          issueType: me.issueTypeId,
          subject: me.subject,
          priority: me.priority,
          description: me.description,
          useAiReview: me.useAiReview,
          aiReviewCompleted: !!aiReviewCompleted,
          fields: me.buildFieldsPayload(),
          email: me.hasSession ? null : me.email,
          recaptchaToken: recaptchaToken,
          honeypot: me.readHoneypot(),
          attachmentTokens: me.attachmentTokens,
          aiFollowup: me.buildAiFollowupPayload(),
        };

        var result = await window.vcCommunicator.submitSupportTicket(payload);
        me.submitResult = result;
      } catch (error) {
        me.submitError = '' + error;
      } finally {
        me.submitting = false;
      }
    },
  },
  mounted() {
    var me = this;
    var vcCommunicator = window.vcCommunicator;
    if (!vcCommunicator) {
      console.log('No vcCommunicator');
      return;
    }

    me.sessionId = vcCommunicator.sessionId;
    me.sessionState = vcCommunicator.sessionState;
    me.isContainerInited = vcCommunicator.isInited || me.sessionId;
    var urlParams = new URLSearchParams(window.location.search);
    me.urlIssueType = (urlParams.get('type') || '').trim();
    me.subject = (urlParams.get('subject') || '').slice(0, 200);

    vcCommunicator.addHandler('createsession', function (sessionId, sessionState) {
      me.sessionId = sessionId;
      me.sessionState = sessionState;
      me.isContainerInited = true;
      me.resetIssueTypeIfNeeded();
      me.applyUrlIssueType();
    });
    vcCommunicator.addHandler('createsessionfailed', function () {
      me.sessionId = null;
      me.sessionState = null;
      me.isContainerInited = true;
    });
    vcCommunicator.addHandler('terminatesession', function () {
      me.sessionId = null;
      me.sessionState = null;
      me.resetIssueTypeIfNeeded();
    });

    me.loadSchema();
  },
};
</script>

<style scoped>
.vc-support-ticket-honeypot {
  position: absolute;
  left: -9999px;
  width: 1px;
  height: 1px;
  opacity: 0;
}

.support-attachment-heading {
  align-items: center;
  display: flex;
  gap: 0.75rem;
  justify-content: space-between;
  margin-bottom: 0.35rem;
}

.support-submit-panel {
  border-top: 1px solid rgba(31, 41, 55, 0.12);
  margin-top: 1.25rem;
  padding-top: 1rem;
}

.support-topic-grid {
  display: grid;
  gap: 0.75rem;
  grid-template-columns: repeat(5, minmax(0, 1fr));
}

.support-topic-option {
  background: #fff;
  border: 1px solid rgba(31, 41, 55, 0.16);
  border-radius: 0.5rem;
  color: inherit;
  display: flex;
  flex-direction: column;
  min-height: 5.5rem;
  padding: 0.85rem 1rem;
  text-align: left;
}

.support-topic-option.active {
  background: #eef8fc;
  border-color: #087aa3;
  box-shadow: 0 0 0 0.15rem rgba(8, 122, 163, 0.15);
}

.support-topic-title {
  font-weight: 600;
  margin-bottom: 0.25rem;
}

.support-topic-description,
.support-topic-empty {
  color: #6f6b85;
  font-size: 0.875rem;
}

@media (max-width: 1100px) {
  .support-topic-grid {
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  }
}

.support-topic-empty {
  border-top: 1px solid rgba(31, 41, 55, 0.12);
  padding-top: 1rem;
}

.support-ai-review-result {
  background: linear-gradient(90deg, #f0f9ff 0%, #fff 100%);
  border: 1px solid rgba(8, 122, 163, 0.28);
  border-left: 0.35rem solid #087aa3;
  border-radius: 0.6rem;
  box-shadow: 0 0.5rem 1.25rem rgba(15, 23, 42, 0.08);
  margin-bottom: 1rem;
  padding: 1rem;
}

.support-ai-review-kicker {
  color: #087aa3;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  margin-bottom: 0.65rem;
  text-transform: uppercase;
}

.support-submit-row {
  align-items: center;
  display: flex;
  gap: 0.75rem 1rem;
}

.support-ai-check {
  flex: 1 1 auto;
  margin-bottom: 0;
  min-width: 0;
}

.support-ai-note {
  color: #6f6b85;
  font-size: 0.875rem;
  line-height: 1.35;
  margin-top: 0.45rem;
}

.support-query-url-row {
  align-items: center;
  display: flex;
  gap: 0.75rem;
}

.support-query-url-row .btn {
  flex: 0 0 auto;
}

.support-query-test-result {
  border-radius: 0.375rem;
  font-size: 0.875rem;
  padding: 0.6rem 0.75rem;
}

.support-query-test-success {
  background: #edf8f1;
  color: #17633a;
}

.support-query-test-error {
  background: #fff1f0;
  color: #8f231c;
}

@media (max-width: 575.98px) {
  .support-attachment-heading {
    align-items: flex-start;
    flex-direction: column;
  }

  .support-submit-row {
    align-items: flex-start;
    flex-direction: column;
  }

  .support-query-url-row {
    align-items: stretch;
    flex-direction: column;
  }
}
</style>
